<div class="docs" [dataSelector]="'cxp-crl-documents-tab'">
  @if (alert(); as alert) {
    <div class="docs-flag-banner" [dataSelector]="'cxp-crl-documents-alert'">
      <aiwyn-icon name="circle-exclamation-fill" class="docs-flag-banner-icon" [height]="18" [width]="18"></aiwyn-icon>
      <div class="docs-flag-banner-body">
        <p class="docs-flag-banner-headline" [dataSelector]="'cxp-crl-documents-alert-headline'">{{ alert.headline }}</p>
        <p class="docs-flag-banner-copy" [dataSelector]="'cxp-crl-documents-alert-copy'">{{ alert.copy }}</p>
        @if (alert.firmMessage; as firmMessage) {
          <p class="docs-flag-banner-note" [dataSelector]="'cxp-crl-documents-alert-note'">{{ firmMessage }}</p>
        }
      </div>
    </div>
  }

  @if (hasReferenceFiles()) {
    <section class="docs-group docs-reference">
      <h4 class="docs-group-head">
        From your firm
        <span class="docs-group-count">{{ referenceCount() }} {{ referenceCount() === 1 ? 'file' : 'files' }}</span>
      </h4>

      <ul class="docs-list">
        @for (file of referenceFiles(); track file.id) {
          <li class="docs-row">
            <awn-file-item-icon [fileName]="file.name" class="docs-row-icon" />

            <div class="docs-row-main">
              <button
                type="button"
                class="docs-row-name"
                [title]="file.name"
                [dataSelector]="'cxp-crl-documents-reference-preview'"
                (click)="previewRequested.emit(file)"
              >
                {{ file.name || 'File' }}
              </button>
              @if (file.size !== null) {
                <span class="docs-row-meta">{{ file.size | fileSize: 1 }}</span>
              }
            </div>

            <button
              type="button"
              class="docs-row-action"
              aria-label="Download file"
              [dataSelector]="'cxp-crl-documents-reference-download'"
              (click)="downloadRequested.emit(file)"
            >
              <aiwyn-icon name="download" class="icon-color-gray-400" [height]="18" [width]="18"></aiwyn-icon>
            </button>
          </li>
        }
      </ul>
    </section>
  }

  @for (group of clientGroups(); track group.key) {
    @let total = group.totalCount + group.staging.length;
    <section class="docs-group">
      <h4 class="docs-group-head">
        {{ group.label }}
        <span class="docs-group-count">{{ total }} {{ total === 1 ? 'file' : 'files' }}</span>
      </h4>

      <ul class="docs-list">
        @for (file of group.files; track file.id) {
          <li class="docs-row" [class.docs-row-flagged]="file.isFlagged">
            @if (file.isFlagged) {
              <awn-file-item-icon
                iconName="flag-pennant"
                iconNamespace="phosphor"
                [cssClasses]="['is-flagged', 'icon-color-red-200', 'icon-fill-red-200']"
                class="docs-row-icon"
              />
            } @else {
              <awn-file-item-icon [fileName]="file.name" class="docs-row-icon" />
            }

            <div class="docs-row-main">
              <button
                type="button"
                class="docs-row-name"
                [class.docs-row-name-flagged]="file.isFlagged"
                [title]="file.name"
                [dataSelector]="'cxp-crl-documents-file-preview'"
                (click)="previewRequested.emit(file)"
              >
                {{ file.name || 'File' }}
              </button>
              <span class="docs-row-meta">
                @if (file.isFlagged) {
                  <span class="docs-row-flag-label">Flagged</span>
                }
                @if (file.size !== null) {
                  @if (file.isFlagged) {
                    <span class="docs-row-meta-sep" aria-hidden="true">·</span>
                  }
                  <span>{{ file.size | fileSize: 1 }}</span>
                }
              </span>
            </div>

            @if (file.isFlagged) {
              <button
                type="button"
                class="docs-row-replace"
                [dataSelector]="'cxp-crl-documents-replace-file'"
                (click)="replaceFileRequested.emit(file)"
              >
                Replace File
              </button>
            } @else if (canEdit()) {
              <button
                type="button"
                class="docs-row-action"
                aria-label="Remove file"
                [dataSelector]="'cxp-crl-documents-file-remove'"
                (click)="removeFileRequested.emit(file)"
              >
                <aiwyn-icon name="x" class="icon-color-gray-400" [height]="18" [width]="18"></aiwyn-icon>
              </button>
            }
          </li>
        }

        @for (row of group.staging; track row.jobId) {
          <li class="docs-row docs-row-uploading">
            @if (row.state === 'done') {
              <aiwyn-icon name="check-circle" class="docs-row-icon icon-color-green-500" [height]="20" [width]="20"></aiwyn-icon>
            } @else {
              <aiwyn-icon name="refresh-cw" class="docs-row-icon docs-row-icon-uploading icon-color-gray-400" [height]="20" [width]="20"></aiwyn-icon>
            }

            <div class="docs-row-main">
              <span class="docs-row-name" [title]="row.name">{{ row.name }}</span>
              @if (row.state === 'error') {
                <span class="docs-row-meta docs-row-meta-error">Upload failed</span>
              } @else if (row.state === 'uploading') {
                <span class="docs-upload-progress" aria-hidden="true">
                  <span class="docs-upload-progress-bar" [style.width.%]="row.percent"></span>
                </span>
                <span class="docs-row-meta">
                  {{ row.bytesLoaded | fileSize: 1 : true }} / {{ row.bytesTotal | fileSize: 1 }} · {{ row.percent }}%
                </span>
              }
            </div>

            @if (row.state === 'uploading') {
              <button
                type="button"
                class="docs-row-action"
                aria-label="Cancel upload"
                [dataSelector]="'cxp-crl-documents-cancel-upload'"
                (click)="cancelUploadRequested.emit(row)"
              >
                <aiwyn-icon name="x" class="icon-color-gray-400" [height]="18" [width]="18"></aiwyn-icon>
              </button>
            }
          </li>
        }
      </ul>
    </section>
  }

  @if (hasMore() && !showAll()) {
    <button
      type="button"
      class="docs-view-all"
      [dataSelector]="'cxp-crl-documents-view-all'"
      (click)="onViewAll()"
    >
      View All Files ({{ clientFileCount() }})
    </button>
  }

  @if (isEmpty()) {
    <p class="docs-empty" [dataSelector]="'cxp-crl-documents-empty'">No files uploaded yet.</p>
  }
</div>
